import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Lock, Mail, Eye, EyeOff } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_fitsmith-academy/artifacts/zbh0gz3y_291E0B11-36FD-4420-BAF1-DDEC0FEFC105.png";
const GOLD = "#C9A84C";
const API = process.env.REACT_APP_BACKEND_URL;

export default function CourseLogin() {
  const { login, user, loading: authLoading } = useAuth();
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(false);
  const [mode, setMode]         = useState("login"); // "login" | "forgot"
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent]   = useState(false);

  // Al ingelogd → doorsturen
  useEffect(() => {
    if (!authLoading && user) {
      const role = user.role;
      if (role === "company_admin") window.location.href = "/bedrijf-admin";
      else if (role === "trainer")  window.location.href = "/trainer";
      else                           window.location.href = "/account";
    }
  }, [user, authLoading]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError("Vul alle velden in"); return; }
    setLoading(true);
    setError("");
    try {
      const data = await login(email, password);
      const role = data?.user?.role;
      if (role === "company_admin") window.location.href = "/bedrijf-admin";
      else if (role === "trainer")  window.location.href = "/trainer";
      else                           window.location.href = "/account";
    } catch (err) {
      setError(err.message || "Inloggen mislukt");
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    if (!forgotEmail) { setError("Vul je e-mailadres in"); return; }
    setLoading(true);
    setError("");
    try {
      await fetch(`${API}/api/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail }),
      });
      setForgotSent(true);
    } catch {
      setError("Er ging iets mis. Probeer opnieuw.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#080808] flex flex-col items-center justify-center px-4">
      {/* Achtergrond glow */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-[#C9A84C]/5 rounded-full blur-[120px]" />
      </div>

      <div className="relative z-10 w-full max-w-sm">
        {/* Logo */}
        <div className="flex flex-col items-center mb-10">
          <img src={LOGO_URL} alt="FitSmith" className="h-14 w-14 object-contain mb-4" />
          <h1 className="text-white font-bold text-2xl tracking-wide">
            FitSmith <span style={{ color: GOLD }}>Academy</span>
          </h1>
        </div>

        {/* Card */}
        <div className="bg-white/3 border border-white/8 rounded-2xl p-6">
          {mode === "login" ? (
            <>
              <h2 className="text-white font-bold text-lg mb-5">Inloggen</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* E-mail */}
                <div>
                  <label className="block text-zinc-400 text-xs font-semibold uppercase tracking-widest mb-1.5">E-mailadres</label>
                  <div className="relative">
                    <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                    <input
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="jouw@email.com"
                      autoComplete="email"
                      data-testid="login-email"
                      className="w-full bg-[#111] border border-white/8 rounded-xl pl-9 pr-4 py-3 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50 transition-colors placeholder-zinc-600"
                    />
                  </div>
                </div>

                {/* Wachtwoord */}
                <div>
                  <label className="block text-zinc-400 text-xs font-semibold uppercase tracking-widest mb-1.5">Wachtwoord</label>
                  <div className="relative">
                    <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                    <input
                      type={showPass ? "text" : "password"}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="••••••••"
                      autoComplete="current-password"
                      data-testid="login-password"
                      className="w-full bg-[#111] border border-white/8 rounded-xl pl-9 pr-10 py-3 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50 transition-colors placeholder-zinc-600"
                    />
                    <button type="button" onClick={() => setShowPass(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300">
                      {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>

                {error && (
                  <p className="text-red-400 text-xs text-center">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="login-submit"
                  className="w-full py-3 rounded-xl font-bold text-sm uppercase tracking-wide transition-all active:scale-95 disabled:opacity-50"
                  style={{ background: GOLD, color: "#000" }}
                >
                  {loading ? "Bezig..." : "Inloggen"}
                </button>
              </form>

              <button
                onClick={() => { setMode("forgot"); setError(""); }}
                className="mt-4 w-full text-center text-zinc-500 text-xs hover:text-zinc-300 transition-colors"
              >
                Wachtwoord vergeten?
              </button>
            </>
          ) : (
            <>
              <h2 className="text-white font-bold text-lg mb-2">Wachtwoord vergeten</h2>
              <p className="text-zinc-500 text-sm mb-5">Vul je e-mailadres in en ontvang een resetlink.</p>
              {forgotSent ? (
                <div className="text-center py-4">
                  <p className="text-green-400 text-sm font-semibold">E-mail verstuurd!</p>
                  <p className="text-zinc-500 text-xs mt-1">Controleer je inbox (en spam).</p>
                </div>
              ) : (
                <form onSubmit={handleForgot} className="space-y-4">
                  <div className="relative">
                    <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={e => setForgotEmail(e.target.value)}
                      placeholder="jouw@email.com"
                      data-testid="forgot-email-input"
                      className="w-full bg-[#111] border border-white/8 rounded-xl pl-9 pr-4 py-3 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50 transition-colors placeholder-zinc-600"
                    />
                  </div>
                  {error && <p className="text-red-400 text-xs text-center">{error}</p>}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 rounded-xl font-bold text-sm uppercase tracking-wide transition-all active:scale-95 disabled:opacity-50"
                    style={{ background: GOLD, color: "#000" }}
                  >
                    {loading ? "Versturen..." : "Resetlink versturen"}
                  </button>
                </form>
              )}
              <button
                onClick={() => { setMode("login"); setError(""); setForgotSent(false); }}
                className="mt-4 w-full text-center text-zinc-500 text-xs hover:text-zinc-300 transition-colors"
              >
                Terug naar inloggen
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
